import { useState } from 'react';
import {
    createMockPayout,
    MockPayoutResponse,
    PAYMENT_METHOD_OPTIONS,
    CARD_FAILURE_OPTIONS,
    ACH_FAILURE_OPTIONS,
} from '../../services/finance-services';

interface CreateMockPayoutModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreated: () => void;
}

const CreateMockPayoutModal = ({ isOpen, onClose, onCreated }: CreateMockPayoutModalProps) => {
    // Payment method selection
    const [selectedMethods, setSelectedMethods] = useState<Set<string>>(
        new Set(PAYMENT_METHOD_OPTIONS.map((pm) => pm.key))
    );

    // Per-method failure scenarios
    const [failureScenarios, setFailureScenarios] = useState<Record<string, string>>({});

    // Configuration
    const [amount, setAmount] = useState('50');
    const [tipMode, setTipMode] = useState<'random' | 'fixed' | 'none'>('random');
    const [fixedTip, setFixedTip] = useState('5');
    const [donorName, setDonorName] = useState('NXT Sync Test');
    const [donorEmail, setDonorEmail] = useState('test+nxt-sync@bowst.com');
    const [ntaf, setNtaf] = useState('');
    const [campaignId, setCampaignId] = useState('');

    // State
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<MockPayoutResponse | null>(null);

    if (!isOpen) return null;

    const toggleMethod = (key: string) => {
        setSelectedMethods((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
                // Remove failure scenario if unchecked
                setFailureScenarios((fs) => {
                    const copy = { ...fs };
                    delete copy[key];
                    return copy;
                });
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const selectAll = () => {
        setSelectedMethods(new Set(PAYMENT_METHOD_OPTIONS.map((pm) => pm.key)));
    };

    const selectNone = () => {
        setSelectedMethods(new Set());
        setFailureScenarios({});
    };

    const getFailureOptions = (type: string) => {
        return type === 'ach' ? ACH_FAILURE_OPTIONS : CARD_FAILURE_OPTIONS;
    };

    const handleSubmit = async () => {
        if (selectedMethods.size === 0) {
            setError('Select at least one payment method');
            return;
        }

        setCreating(true);
        setError(null);
        setResult(null);

        try {
            // Build failure_scenarios: only include methods that have a failure selected
            const failureScenariosPayload: Record<string, string> = {};
            for (const [key, value] of Object.entries(failureScenarios)) {
                if (value && selectedMethods.has(key)) {
                    failureScenariosPayload[key] = value;
                }
            }

            const response = await createMockPayout({
                payment_methods: Array.from(selectedMethods),
                amount: parseFloat(amount) || 50,
                tip: tipMode === 'fixed' ? parseFloat(fixedTip) || 0 : null,
                no_tip: tipMode === 'none',
                donor_name: donorName || 'NXT Sync Test',
                donor_email: donorEmail || 'test+nxt-sync@bowst.com',
                ntaf: ntaf || undefined,
                campaign_id: campaignId ? parseInt(campaignId, 10) : null,
                failure_scenarios:
                    Object.keys(failureScenariosPayload).length > 0
                        ? failureScenariosPayload
                        : undefined,
            });

            setResult(response);
            onCreated();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to create mock payout');
        } finally {
            setCreating(false);
        }
    };

    const handleClose = () => {
        setResult(null);
        setError(null);
        onClose();
    };

    return (
        <div className='fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50'>
            <div className='max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl dark:bg-boxdark'>
                <div className='mb-4 flex items-center justify-between'>
                    <h3 className='text-xl font-semibold text-black dark:text-white'>
                        Create Mock Payout
                    </h3>
                    <button
                        onClick={handleClose}
                        className='text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                    >
                        <svg className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                        </svg>
                    </button>
                </div>

                {/* Results view */}
                {result ? (
                    <div>
                        <div className='mb-4 rounded-lg border border-success bg-success bg-opacity-10 p-4'>
                            <p className='font-medium text-success'>
                                Mock payout created: <code className='text-sm'>{result.payout_id}</code>
                            </p>
                            <p className='mt-1 text-sm text-gray-600 dark:text-gray-400'>
                                {result.summary.processed} succeeded, {result.summary.failed} failed
                                {' · '}${result.summary.total_amount.toFixed(2)} total
                                {result.summary.total_tips > 0 && ` · $${result.summary.total_tips.toFixed(2)} tips`}
                            </p>
                        </div>

                        <div className='overflow-x-auto'>
                            <table className='w-full text-sm'>
                                <thead>
                                    <tr className='border-b border-stroke dark:border-strokedark'>
                                        <th className='px-2 py-2 text-left font-medium'>Method</th>
                                        <th className='px-2 py-2 text-right font-medium'>Amount</th>
                                        <th className='px-2 py-2 text-right font-medium'>Tip</th>
                                        <th className='px-2 py-2 text-right font-medium'>Fee</th>
                                        <th className='px-2 py-2 text-center font-medium'>Status</th>
                                        <th className='px-2 py-2 text-left font-medium'>Details</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {result.charges.map((charge) => (
                                        <tr
                                            key={charge.charge_id}
                                            className='border-b border-stroke dark:border-strokedark'
                                        >
                                            <td className='px-2 py-2'>{charge.label}</td>
                                            <td className='px-2 py-2 text-right'>${charge.amount.toFixed(2)}</td>
                                            <td className='px-2 py-2 text-right'>
                                                {charge.tip > 0 ? `$${charge.tip.toFixed(2)}` : '-'}
                                            </td>
                                            <td className='px-2 py-2 text-right'>${charge.fee.toFixed(2)}</td>
                                            <td className='px-2 py-2 text-center'>
                                                <span
                                                    className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                                                        charge.status === 'succeeded'
                                                            ? 'bg-success text-white'
                                                            : 'bg-danger text-white'
                                                    }`}
                                                >
                                                    {charge.status}
                                                </span>
                                            </td>
                                            <td className='px-2 py-2 text-xs text-gray-500'>
                                                {charge.failure_scenario && (
                                                    <span className='text-danger'>{charge.failure_message || charge.failure_scenario}</span>
                                                )}
                                                {charge.error && !charge.failure_scenario && (
                                                    <span className='text-danger'>{charge.error}</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className='mt-4 flex justify-end'>
                            <button
                                onClick={handleClose}
                                className='rounded bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-opacity-90'
                            >
                                Close
                            </button>
                        </div>
                    </div>
                ) : (
                    /* Configuration form */
                    <div className='space-y-5'>
                        {error && (
                            <div className='rounded border border-danger bg-danger bg-opacity-10 px-4 py-3 text-sm text-danger'>
                                {error}
                            </div>
                        )}

                        {/* Payment Methods */}
                        <div>
                            <div className='mb-2 flex items-center justify-between'>
                                <label className='text-sm font-medium text-black dark:text-white'>
                                    Payment Methods
                                </label>
                                <div className='flex gap-2'>
                                    <button
                                        type='button'
                                        onClick={selectAll}
                                        className='text-xs text-primary hover:underline'
                                    >
                                        Select All
                                    </button>
                                    <span className='text-xs text-gray-400'>|</span>
                                    <button
                                        type='button'
                                        onClick={selectNone}
                                        className='text-xs text-primary hover:underline'
                                    >
                                        None
                                    </button>
                                </div>
                            </div>
                            <div className='space-y-2'>
                                {PAYMENT_METHOD_OPTIONS.map((pm) => (
                                    <div
                                        key={pm.key}
                                        className='flex items-center gap-3 rounded border border-stroke px-3 py-2 dark:border-strokedark'
                                    >
                                        <input
                                            type='checkbox'
                                            id={`pm-${pm.key}`}
                                            checked={selectedMethods.has(pm.key)}
                                            onChange={() => toggleMethod(pm.key)}
                                            className='h-4 w-4 rounded border-gray-300'
                                        />
                                        <label
                                            htmlFor={`pm-${pm.key}`}
                                            className='flex-1 cursor-pointer text-sm'
                                        >
                                            {pm.label}
                                        </label>
                                        {selectedMethods.has(pm.key) && (
                                            <select
                                                value={failureScenarios[pm.key] || ''}
                                                onChange={(e) =>
                                                    setFailureScenarios((prev) => ({
                                                        ...prev,
                                                        [pm.key]: e.target.value,
                                                    }))
                                                }
                                                className='rounded border border-stroke bg-transparent px-2 py-1 text-xs outline-none focus:border-primary dark:border-strokedark dark:bg-boxdark'
                                            >
                                                <option value=''>Success</option>
                                                <optgroup label='Failure Scenarios'>
                                                    {getFailureOptions(pm.type).map((opt) => (
                                                        <option key={opt.key} value={opt.key}>
                                                            {opt.label} — {opt.description}
                                                        </option>
                                                    ))}
                                                </optgroup>
                                            </select>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Amount & Tip */}
                        <div className='grid grid-cols-2 gap-4'>
                            <div>
                                <label className='mb-1 block text-sm font-medium text-black dark:text-white'>
                                    Amount ($)
                                </label>
                                <input
                                    type='number'
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    min='1'
                                    step='0.01'
                                    className='w-full rounded border border-stroke bg-transparent px-3 py-2 text-sm outline-none focus:border-primary dark:border-strokedark dark:bg-boxdark'
                                />
                            </div>
                            <div>
                                <label className='mb-1 block text-sm font-medium text-black dark:text-white'>
                                    Tip
                                </label>
                                <div className='flex items-center gap-2'>
                                    <select
                                        value={tipMode}
                                        onChange={(e) => setTipMode(e.target.value as 'random' | 'fixed' | 'none')}
                                        className='rounded border border-stroke bg-transparent px-2 py-2 text-sm outline-none focus:border-primary dark:border-strokedark dark:bg-boxdark'
                                    >
                                        <option value='random'>Random</option>
                                        <option value='fixed'>Fixed</option>
                                        <option value='none'>No Tip</option>
                                    </select>
                                    {tipMode === 'fixed' && (
                                        <input
                                            type='number'
                                            value={fixedTip}
                                            onChange={(e) => setFixedTip(e.target.value)}
                                            min='0'
                                            step='0.01'
                                            placeholder='$'
                                            className='w-20 rounded border border-stroke bg-transparent px-2 py-2 text-sm outline-none focus:border-primary dark:border-strokedark dark:bg-boxdark'
                                        />
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Donor Info */}
                        <div className='grid grid-cols-2 gap-4'>
                            <div>
                                <label className='mb-1 block text-sm font-medium text-black dark:text-white'>
                                    Donor Name
                                </label>
                                <input
                                    type='text'
                                    value={donorName}
                                    onChange={(e) => setDonorName(e.target.value)}
                                    className='w-full rounded border border-stroke bg-transparent px-3 py-2 text-sm outline-none focus:border-primary dark:border-strokedark dark:bg-boxdark'
                                />
                            </div>
                            <div>
                                <label className='mb-1 block text-sm font-medium text-black dark:text-white'>
                                    Donor Email
                                </label>
                                <input
                                    type='email'
                                    value={donorEmail}
                                    onChange={(e) => setDonorEmail(e.target.value)}
                                    className='w-full rounded border border-stroke bg-transparent px-3 py-2 text-sm outline-none focus:border-primary dark:border-strokedark dark:bg-boxdark'
                                />
                            </div>
                        </div>

                        {/* NTAF & Campaign ID */}
                        <div className='grid grid-cols-2 gap-4'>
                            <div>
                                <label className='mb-1 block text-sm font-medium text-black dark:text-white'>
                                    NTAF{' '}
                                    <span className='font-normal text-gray-500'>(optional — finds campaign by NTAF)</span>
                                </label>
                                <input
                                    type='text'
                                    value={ntaf}
                                    onChange={(e) => {
                                        setNtaf(e.target.value);
                                        if (e.target.value) setCampaignId('');
                                    }}
                                    placeholder='e.g. 12345'
                                    disabled={!!campaignId}
                                    className='w-full rounded border border-stroke bg-transparent px-3 py-2 text-sm outline-none focus:border-primary disabled:opacity-50 dark:border-strokedark dark:bg-boxdark'
                                />
                            </div>
                            <div>
                                <label className='mb-1 block text-sm font-medium text-black dark:text-white'>
                                    Campaign ID{' '}
                                    <span className='font-normal text-gray-500'>(optional — auto-selects if empty)</span>
                                </label>
                                <input
                                    type='number'
                                    value={campaignId}
                                    onChange={(e) => {
                                        setCampaignId(e.target.value);
                                        if (e.target.value) setNtaf('');
                                    }}
                                    placeholder='Auto-select'
                                    disabled={!!ntaf}
                                    className='w-full rounded border border-stroke bg-transparent px-3 py-2 text-sm outline-none focus:border-primary disabled:opacity-50 dark:border-strokedark dark:bg-boxdark'
                                />
                            </div>
                        </div>

                        {/* Actions */}
                        <div className='flex items-center justify-between border-t border-stroke pt-4 dark:border-strokedark'>
                            <span className='text-xs text-gray-500'>
                                {selectedMethods.size} method{selectedMethods.size !== 1 ? 's' : ''} selected
                                {Object.values(failureScenarios).filter(Boolean).length > 0 &&
                                    ` · ${Object.values(failureScenarios).filter(Boolean).length} with failures`}
                            </span>
                            <div className='flex gap-3'>
                                <button
                                    onClick={handleClose}
                                    disabled={creating}
                                    className='rounded border border-stroke px-4 py-2 text-sm font-medium transition-colors hover:bg-gray-100 dark:border-strokedark dark:hover:bg-meta-4'
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={creating || selectedMethods.size === 0}
                                    className='inline-flex items-center gap-2 rounded bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-opacity-90 disabled:opacity-50'
                                >
                                    {creating ? (
                                        <>
                                            <div className='h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent'></div>
                                            Creating...
                                        </>
                                    ) : (
                                        'Create Mock Payout'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CreateMockPayoutModal;
